import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@services/user.service';
import { Router } from '@angular/router';
import { NotificationsService } from '@services/notifications.service';
import { TranslationService } from '@services/translation.service';
import { matchFormControlValidator } from '@app/validators/must-match-input.directive';
import {
  CognitoPasswordPolicy,
  getNewPwValidatorsFromPolicy,
} from '@app/shared/password-form.utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false,
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  policies: CognitoPasswordPolicy;
  passwordStrength: number = 0;
  passwordForm: FormGroup;
  isProcessing: boolean;
  isLoading = true;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private notification: NotificationsService,
    private userService: UserService,
    public location: Location,
    private router: Router,
    private translation: TranslationService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.userService
      .getPasswordPolicy()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.isLoading = false;
          this.policies = data;
          this.buildPasswordForm();
        },
        error: () => {
          this.isLoading = false;
          this.notification.popNotification(
            '',
            this.translation.getTranslatedContent(
              'error_loading_password_policy',
              'Unable to load password requirements. Please try again.'
            )
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildPasswordForm(): void {
    if (!this.policies) return;
    const newPwValidators = getNewPwValidatorsFromPolicy(this.policies);

    this.passwordForm = this.fb.group({
      oldPw: ['', Validators.required],
      newPw: ['', newPwValidators],
      confirmPw: ['', [Validators.required, matchFormControlValidator('newPw')]],
    });

    this.passwordForm
      .get('newPw')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.passwordForm.get('confirmPw').updateValueAndValidity());
  }

  resetPw(): void {
    const oldPw = this.passwordForm.get('oldPw').value;
    const newPw = this.passwordForm.get('newPw').value;
    this.isProcessing = true;
    this.userService
      .changePassword(oldPw, newPw)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          if (result?.status === 'password reset') {
            this.notification.popNotification('', 'Updated Password');
            this.router.navigate(['/home']);
          } else {
            this.notification.popNotification(
              '',
              this.getErrorMessageFromResult(result)
            );
          }
        },
        error: err => {
          this.isProcessing = false;
          this.notification.popNotification('', this.getErrorMessageFromError(err));
        },
        complete: () => (this.isProcessing = false),
      });
  }

  private getErrorMessageFromResult(result: { error?: string }): string {
    if (result?.error === 'LimitExceededException') {
      return this.translation.getTranslatedContent(
        'too_many_attempts_at_this_time',
        'Too many attempts at this time.'
      );
    }
    if (result?.error === 'NotAuthorizedException') {
      return this.translation.getTranslatedContent(
        'incorrect_current_password',
        'Incorrect current password.'
      );
    }
    return this.translation.getTranslatedContent(
      'error_updating_password',
      'Error Updating Password.'
    );
  }

  private getErrorMessageFromError(err: { error?: { message?: string }; message?: string }): string {
    return (
      err?.error?.message ??
      err?.message ??
      this.translation.getTranslatedContent(
        'error_updating_password',
        'Error updating password. Please try again.'
      )
    );
  }
}
